import React, { useCallback, useRef, useEffect, useContext } from 'react';
import PerfectScrollbar from 'react-perfect-scrollbar';
import { useSelector } from 'react-redux';
import { useFilteredAndSortedData } from 'features/case/redux/setFilteredAndSortedData';
import {
  selectDocuments,
  selectCurrentSelectedFileMetaData,
  selectLastSelectedFileMetaData,
} from 'common/selectors';

// Create a context to pass the data length around
export const ScrollDataContext = React.createContext<{ dataLength: number }>({ dataLength: 0 });

// Create the CustomScrollbars component that will be used by CustomScrollbarsVirtualList
export const CustomScrollbars = ({
  onScroll,
  style,
  children,
  ...rest
}: {
  onScroll?: () => void;
  style?: React.CSSProperties;
  children: React.ReactNode;
  [key: string]: any;
}) => {
  const scrollbarRef = useRef<any>(null);

  return (
    <PerfectScrollbar
      containerRef={(ref) => {
        scrollbarRef.current = ref;
      }}
      style={style}
      options={{ minScrollbarLength: 20 }}
      onScroll={onScroll}
      {...rest}
    >
      {children}
    </PerfectScrollbar>
  );
};

// The main component that will be used as outerElementType for List
const CustomScrollbarsVirtualList = React.forwardRef<any, any>((props, ref) => {
  const { children, style, onScroll, ...rest } = props;
  const documents = useSelector(selectDocuments);
  const currentSelectedFileMetaData = useSelector(selectCurrentSelectedFileMetaData);
  const lastSelectedFileMetaData = useSelector(selectLastSelectedFileMetaData);
  const { filteredAndSortedData } = useFilteredAndSortedData();
  const scrollbarRef = useRef<any>(null);
  // Get dataLength from context or provide a fallback
  const { dataLength = 0 } = useContext(ScrollDataContext);

  // Track previous dataLength to detect changes
  const prevDataLengthRef = useRef<number>(dataLength);

  // Reset scroll when filtered data is small
  useEffect(() => {
    if (
      dataLength !== prevDataLengthRef.current &&
      dataLength > 0 &&
      dataLength < 10 &&
      scrollbarRef.current
    ) {
      scrollbarRef.current.scrollTop = 0;
    }
    prevDataLengthRef.current = dataLength;
  }, [dataLength]);

  // Handle scrolling when selected file metadata changes (current or last-selected)
  useEffect(() => {
    const selectedMeta =
      currentSelectedFileMetaData && Object.keys(currentSelectedFileMetaData).length > 0
        ? currentSelectedFileMetaData
        : lastSelectedFileMetaData && Object.keys(lastSelectedFileMetaData).length > 0
          ? lastSelectedFileMetaData
          : null;

    if (scrollbarRef.current && selectedMeta) {
      let fileIndex = -1;
      const list =
        filteredAndSortedData &&
        Array.isArray(filteredAndSortedData) &&
        filteredAndSortedData.length > 0
          ? filteredAndSortedData
          : documents;

      // First try folderFileId
      if (selectedMeta.folderFileId) {
        fileIndex = list.findIndex((item: any) => item.folderFileId === selectedMeta.folderFileId);
      }

      // If not found, try compositeKey
      if (fileIndex === -1 && selectedMeta.compositeKey) {
        fileIndex = list.findIndex((item: any) => item.compositeKey === selectedMeta.compositeKey);
      }

      // If still not found, try id
      if (fileIndex === -1 && selectedMeta.id) {
        fileIndex = list.findIndex((item: any) => item.id === selectedMeta.id);
      }

      // Only scroll if the target row is outside the current viewport (with margins)
      if (fileIndex >= 0) {
        const rowHeight = 37;
        const targetPosition = fileIndex * rowHeight;
        const containerHeight = scrollbarRef.current.clientHeight || 0;
        const currentTop = scrollbarRef.current.scrollTop || 0;
        const currentBottom = currentTop + containerHeight;
        const targetTop = targetPosition;
        const targetBottom = targetPosition + rowHeight;

        // Treat as not visible if too close to edges to avoid being hidden under UI chrome
        const topMargin = 8; // px
        const bottomMargin = rowHeight; // one row ensures next row reveal on Next
        const isVisible =
          targetTop >= currentTop + topMargin && targetBottom <= currentBottom - bottomMargin;
        if (!isVisible) {
          // Center the row in the viewport
          const centerTop = Math.max(
            0,
            targetPosition - Math.max(0, containerHeight / 2 - rowHeight / 2),
          );
          const maxScroll = Math.max(0, (scrollbarRef.current.scrollHeight || 0) - containerHeight);
          const clamped = Math.max(0, Math.min(centerTop, maxScroll));
          scrollbarRef.current.scrollTo({ top: clamped });
        }
      }
    }
  }, [
    currentSelectedFileMetaData,
    lastSelectedFileMetaData,
    documents,
    filteredAndSortedData,
    dataLength,
  ]);

  // Handle URL hash-based scrolling whenever documents change or hash changes
  useEffect(() => {
    if (scrollbarRef.current && documents.length > 0) {
      const scrollToIndex = (index: number) => {
        if (index >= 0) {
          const rowHeight = 37;
          const targetPosition = index * rowHeight;
          const containerHeight = scrollbarRef.current.clientHeight || 0;
          const centerTop = Math.max(
            0,
            targetPosition - Math.max(0, containerHeight / 2 - rowHeight / 2),
          );
          const maxScroll = Math.max(0, (scrollbarRef.current.scrollHeight || 0) - containerHeight);
          const clamped = Math.max(0, Math.min(centerTop, maxScroll));
          scrollbarRef.current.scrollTo({ top: clamped });
        }
      };

      const currentUrlHashVal = window.location.hash;
      const hashFileId = currentUrlHashVal.split('#')[1];

      if (hashFileId) {
        const list =
          filteredAndSortedData &&
          Array.isArray(filteredAndSortedData) &&
          filteredAndSortedData.length > 0
            ? filteredAndSortedData
            : documents;
        // Try to find the file by folderFileId, compositeKey, or id
        const fileIndex = list.findIndex(
          (item: any) =>
            item.folderFileId === hashFileId ||
            item.compositeKey === hashFileId ||
            item.id === hashFileId,
        );

        if (fileIndex >= 0) {
          scrollToIndex(fileIndex);
        }
      }
    }
  }, [documents, filteredAndSortedData]);

  // Listen for URL hash changes
  useEffect(() => {
    const handleHashChange = () => {
      if (scrollbarRef.current && documents.length > 0) {
        const currentUrlHashVal = window.location.hash;
        const hashFileId = currentUrlHashVal.split('#')[1];

        if (hashFileId) {
          const list =
            filteredAndSortedData &&
            Array.isArray(filteredAndSortedData) &&
            filteredAndSortedData.length > 0
              ? filteredAndSortedData
              : documents;
          const fileIndex = list.findIndex(
            (item: any) =>
              item.folderFileId === hashFileId ||
              item.compositeKey === hashFileId ||
              item.id === hashFileId,
          );

          if (fileIndex >= 0) {
            const rowHeight = 37;
            const targetPosition = fileIndex * rowHeight;
            const containerHeight = scrollbarRef.current.clientHeight || 0;
            const centerTop = Math.max(
              0,
              targetPosition - Math.max(0, containerHeight / 2 - rowHeight / 2),
            );
            const maxScroll = Math.max(
              0,
              (scrollbarRef.current.scrollHeight || 0) - containerHeight,
            );
            const clamped = Math.max(0, Math.min(centerTop, maxScroll));
            scrollbarRef.current.scrollTo({ top: clamped });
          }
        }
      }
    };

    // Listen for hash changes
    window.addEventListener('hashchange', handleHashChange);

    // Clean up the event listener
    return () => {
      window.removeEventListener('hashchange', handleHashChange);
    };
  }, [documents, filteredAndSortedData, dataLength]);

  const refSetter = useCallback(
    (scrollbarsRef) => {
      if (scrollbarsRef) {
        // Store the scrollbar reference
        scrollbarRef.current = scrollbarsRef;

        // Forward the ref
        if (ref) {
          if (typeof ref === 'function') {
            ref(scrollbarsRef);
          } else {
            ref.current = scrollbarsRef;
          }
        }
      }
    },
    [ref],
  );

  return (
    <PerfectScrollbar
      containerRef={refSetter}
      style={{ ...style, overflowX: 'auto' }}
      options={{ minScrollbarLength: 20, suppressScrollX: false }}
      onScroll={onScroll}
      {...rest}
    >
      {children}
    </PerfectScrollbar>
  );
});

export default CustomScrollbarsVirtualList;
